import { ImagePosition, Card } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { ReactNode, useState } from "react";
import { ImageQueryResponse } from "../../types/ImageQueryReponse";
import classnames from "classnames";

import style from "./style.module.scss";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";

export interface GatsbyImageCardProps {
    cookieMessage?: ReactNode; // a React Node rendered instead of the card image; typically a WarningMessage
    image: ImageQueryResponse;
    imageFocus?: ImagePosition;
    video?: ReactNode;
    hasOverlay?: boolean;
    fillImageSpace?: boolean; // whether the image should fill the available space or not
    fullBackground?: boolean;
    children?: ReactNode;
    backgroundStyle?: Record<string, any>;
    component?: React.ElementType;
    checkCardPosition?: (cardHorizontal: boolean) => void;
    overlay?: "none" | "dark" | "light";
    idPrefix?: string;
    imagePlacement?: "default" | "50_img_left" | "50_img_right";
    padding?: "default" | "s" | "m" | "l" | "xl";
    loading?: "lazy" | "eager";
    fallbackImageComponent?: JSX.Element;
    contentLinksNotUnderlined?: boolean;
    [x: string]: any; //to allow any other props: https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#strict-object-literal-assignment-checking
}

/**
 * A Card that inserts its own Gatsby (Background) Image component into the Design System Card.
 * Since Gatsby Image offers excellent image loading features, we prefer to use this Card in Gatsby based projects.
 */
export const GatsbyImageCard = ({
    image,
    imageFocus = ImagePosition.CENTER_CENTER,
    imagePlacement = "default",
    padding = "default",
    video,
    hasOverlay = false,
    overlay = "none",
    children = null,
    fullBackground = false,
    backgroundStyle = {
        "--card-background-color": "var(--background-transparent)",
        borderRadius: "var(--card-border-radius)",
        overflow: "hidden",
        height: "100%",
    },
    fillImageSpace = false,
    component = null,
    checkCardPosition = null,
    idPrefix,
    cookieMessage,
    loading = "lazy",
    fallbackImageComponent,
    contentLinksNotUnderlined = false,
    ...other
}: GatsbyImageCardProps): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();

    const [cardHorizontal, setCardHorizontal] = useState(false);
    const Component: any = component || Card;
    const cardPositionHorizontal = (horizontal: boolean): void => {
        checkCardPosition && checkCardPosition(horizontal);
        setCardHorizontal(horizontal);
    };

    const imageData = image?.gatsbyImageData;

    if (cookieMessage) {
        return (
            <Component {...other} cookieMessage={cookieMessage} idPrefix={idPrefix}>
                {children}
            </Component>
        );
    }

    if (video) {
        return (
            <Component
                {...other}
                video={video}
                idPrefix={idPrefix}
                contentLinksNotUnderlined={contentLinksNotUnderlined}
            >
                {children}
            </Component>
        );
    }

    if (!image?.gatsbyImageData) {
        return (
            <Component
                {...other}
                fullBackground={fullBackground}
                image={image?.url}
                imagePosition={imageFocus}
                imagePlacement={imagePlacement}
                padding={padding}
                checkCardPosition={cardPositionHorizontal}
                alt={image?.description}
                idPrefix={idPrefix}
                loading={"eager"}
                fallbackImageComponent={fallbackImageComponent}
                contentLinksNotUnderlined={contentLinksNotUnderlined}
            >
                {children}
                {fullBackground && overlay !== "none" && !hasOverlay && (
                    <div className={classnames(style.overlay, style[`overlay_${overlay}`])} />
                )}
                {fullBackground && overlay === "none" && hasOverlay && (
                    <div className={classnames(style.overlay, style.overlay_gradient)} />
                )}
            </Component>
        );
    }

    if (fullBackground && !jnjFullBranded) {
        return (
            <div className={style.cardWrapper}>
                <Component
                    {...other}
                    fullBackground={fullBackground}
                    imagePosition={imageFocus}
                    imagePlacement={imagePlacement}
                    padding={padding}
                    checkCardPosition={cardPositionHorizontal}
                    alt={image?.description}
                    idPrefix={idPrefix}
                >
                    {children}
                </Component>
                <GatsbyImage
                    alt={image?.description || ""}
                    image={image?.gatsbyImageData}
                    loading={loading}
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        insetInlineStart: 0,
                    }}
                    imgStyle={{ ...backgroundStyle, objectPosition: imageFocus.replace("-", " ") }}
                />
                {overlay !== "none" && !hasOverlay && (
                    <div className={classnames(style.overlay, style[`overlay_${overlay}`])} />
                )}
                {overlay === "none" && hasOverlay && (
                    <div className={classnames(style.overlay, style.overlay_gradient)} />
                )}
            </div>
        );
    }

    let aspectRatio = undefined;
    if (jnjFullBranded) {
        aspectRatio = 16 / 9;
        imageData.height = 0.56;
    }

    return (
        <Component
            {...other}
            fullBackground={!jnjFullBranded ? fullBackground : false}
            image={
                !fullBackground || jnjFullBranded ? (
                    <GatsbyImage
                        alt={image?.description || ""}
                        image={imageData}
                        style={{
                            position: cardHorizontal && !fillImageSpace ? "initial" : "absolute",
                            width: "100%",
                            height: "100%",
                            minWidth: fillImageSpace ? "100%" : null,
                            minHeight: fillImageSpace ? "100%" : null,
                            top: 0,
                            insetInlineStart: 0,
                        }}
                        imgStyle={{ objectPosition: imageFocus.replace("-", " "), aspectRatio }}
                        loading={loading}
                    />
                ) : null
            }
            imagePosition={imageFocus}
            imagePlacement={imagePlacement}
            padding={padding}
            checkCardPosition={cardPositionHorizontal}
            alt={image?.description}
            idPrefix={idPrefix}
            contentLinksNotUnderlined={contentLinksNotUnderlined}
        >
            {children}
        </Component>
    );
};

export default GatsbyImageCard;
